import * as THREE from 'three';
import { TRAIL_CELL_CAPACITY } from './trailCells';

export const createDeformationShader = () => ({
    uniforms: {
        uTexture: { value: null },
        uTextureHover: { value: null },
        uGrid: { value: null },
        uGridSize: { value: new THREE.Vector2() },
        uContainerResolution: { value: new THREE.Vector2(window.innerWidth, window.innerHeight) },
        uCellData: { value: 0 },
        uTime: { value: 0 },
    },
    vertexShader: `
        varying vec2 vUv;

        void main()
        {
            vec4 modelPosition = modelMatrix * vec4(position, 1.0);
            vec4 viewPosition = viewMatrix * modelPosition;
            vec4 projectedPosition = projectionMatrix * viewPosition;
            gl_Position = projectedPosition;

            vUv=uv;
        }
    `,
    fragmentShader: `
        const int NUM_OPACITY = ${TRAIL_CELL_CAPACITY};

        uniform sampler2D uTexture;
        uniform sampler2D uTextureHover;
        uniform sampler2D uGrid;
        varying vec2 vUv;

        uniform vec2 uContainerResolution;
        uniform vec2 uGridSize;
        uniform float uTime;
        uniform vec3 uCellData[NUM_OPACITY];

        float finalMask = 0.0;

        vec2 cellSize(vec2 grid) {
            return 1.0 / grid;
        }

        vec2 cellCenter(int x, int y) {
            vec2 gridSize = vec2(uGridSize.x);
            vec2 cellSize = 1.0 / gridSize;
            vec2 cellPos = vec2(x, y) * cellSize;
            vec2 cellCenter = cellPos + cellSize * 0.5;
            return -cellCenter;
        }

        vec2 stPos() {
            // We manage the device ratio by passing PR constant
            vec2 conRes = uContainerResolution;
            float pixelRatio = float(PR);
            vec2 res = conRes * pixelRatio;
            vec2 st = gl_FragCoord.xy / res.xy;

            // Keep the good ratio of the coordinates
            st.y *= conRes.y / conRes.x;

            return st;
        }

        float roundedSquareBorder(vec2 _st, float _size, float radius) {
            vec2 d = abs(_st) - (_size - radius);
            float dist = 2.0 * length(max(d, 0.0)) - radius;

            return 0.5 - smoothstep(0.0, 0.1, dist);
        }

        void main()
        {
            vec2 newUvs = vUv;

            // The horizontal(s) and vertical(t) texture coordinates
            vec2 st = stPos();

            vec2 cellSize = cellSize(uGridSize) * 1.0;

            for (int i = 0; i < NUM_OPACITY; i++) {
                vec3 cellData = uCellData[i];
                ivec2 cellID = ivec2(int(cellData.x), int(cellData.y));
                vec2 cellPos = st + cellCenter(cellID.x, cellID.y);
                float c = roundedSquareBorder(cellPos, cellSize.x * 0.5, 0.01);
                float opacity = cellData.z;

                finalMask += smoothstep(0.4, 0.5, c) * opacity;
            }

            // Crop the video to cover the screen
            float outAR = uContainerResolution.x / uContainerResolution.y;
            if (outAR > 16.0 / 9.0) {
                // Phone screen is wider than the video
                float scale = (16.0 / 9.0) / outAR;
                newUvs.y = newUvs.y * scale + (1.0 - scale) * 0.5;
            }
            else {
                float scale = outAR / (9.0 / 16.0);
                newUvs.x = newUvs.x * scale + (1.0 - scale) * 0.5;
            }

            vec4 displacement = texture2D(uGrid,newUvs);

            vec2 finalUvs = newUvs - displacement.rg*0.01;

            vec4 finalImage = texture2D(uTexture,finalUvs);

            // Cannot use finalUv because it distorts the image
            vec4 finalImageHover = texture2D(uTextureHover, vec2(newUvs.x, newUvs.y));

            // Calculate gradient position based on time to animate the gradient
            float gradientPos = mod(uTime * 0.1 + newUvs.x * 0.3, 1.0);

            // Blend colors based on gradient position
            vec3 gradientColor = vec3(
                0.5 + 0.5 * sin(2.0 * 3.14159 * (gradientPos + 0.0)),  // Red channel
                0.5 + 0.5 * sin(2.0 * 3.14159 * (gradientPos + 0.33)), // Green channel (offset by 1/3)
                0.5 + 0.5 * sin(2.0 * 3.14159 * (gradientPos + 0.66))  // Blue channel (offset by 2/3)
            );

            // Blend the gradient color with the texture color
            vec4 combinedColor = finalImageHover.a > 0.5 ? finalImageHover : vec4(gradientColor, 1.0);
            combinedColor = clamp(combinedColor, 0.0, 1.0);

            vec4 final = mix(finalImage, combinedColor, finalMask);
            final.a = 1.0;
            gl_FragColor = final;
        }
    `
});

export const GPGPUShader = {
    fragmentShader: `
        uniform vec2 uMouse;
        uniform vec2 uDeltaMouse;
        uniform float uMouseMove;
        uniform float uGridSize;
        uniform float uRelaxation;
        uniform float uDistance;


        void main()
        {
            vec2 uv = gl_FragCoord.xy/resolution.xy;

            vec4 color = texture(uGrid,uv);

            float dist = distance(uv,uMouse);
            dist = 1.-(smoothstep(0.,uDistance/uGridSize,dist));


            vec2 delta = uDeltaMouse;

            color.rg+=delta*dist;
            color.rg*=min(uRelaxation,uMouseMove);
            

            gl_FragColor = color;
        }
    `
};
